"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useInView, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import Hls from "hls.js";
import { stegaClean } from "@sanity/client/stega";

import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import type { GROQMuxVideo } from "@/sanity/lib/definitions";
import heroAssetMobileEn from "@/public/home-new/hero_asset_mobile_en.jpg";
import heroMotionPoster from "@/public/lp-long/hero-mobile-motion-de.webp";

interface HeroMobilePreviewProps {
  language?: string;
  video?: GROQMuxVideo;
}

export default function HeroMobilePreview({ language, video: source }: HeroMobilePreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null!);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile({ breakpoint: 767 });
  const reducedMotion = useReducedMotion();
  const inView = useInView(containerRef);
  const [userPaused, setUserPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isEnglish = stegaClean(language) === "en";
  const playbackId = stegaClean(source?.playbackId ?? "");
  const poster = playbackId
    ? `https://image.mux.com/${playbackId}/thumbnail.webp?time=1&width=828`
    : isEnglish ? heroAssetMobileEn : heroMotionPoster;
  const canAnimate = (!isEnglish || !!playbackId) && isMobile === true && reducedMotion === false && !hasError;

  useEffect(() => {
    const video = videoRef.current;
    if (!canAnimate || !playbackId || !video) return;

    const url = `https://stream.mux.com/${playbackId}.m3u8?redundant_streams=true`;
    if (!Hls.isSupported()) {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = url;
      } else {
        setHasError(true);
      }
      return;
    }

    const hls = new Hls({ abrEwmaDefaultEstimate: 5_000_000, startFragPrefetch: true });
    hls.attachMedia(video);
    hls.loadSource(url);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) setHasError(true);
    });
    return () => hls.destroy();
  }, [canAnimate, playbackId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!canAnimate || !video) return;

    const syncPlayback = () => {
      if (inView && !userPaused && document.visibilityState === "visible") {
        void video.play().catch(() => setIsPlaying(false));
      } else {
        video.pause();
      }
    };

    syncPlayback();
    document.addEventListener("visibilitychange", syncPlayback);
    return () => {
      document.removeEventListener("visibilitychange", syncPlayback);
      video.pause();
    };
  }, [canAnimate, inView, userPaused]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      setUserPaused(false);
      void video.play().catch(() => setIsPlaying(false));
    } else {
      setUserPaused(true);
      video.pause();
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative w-full overflow-hidden rounded-[2rem] bg-[#0A1A1D] md:hidden",
        isEnglish && !playbackId ? "aspect-[406/708]" : "aspect-[414/716]",
      )}>
      <Image
        className="h-full w-full object-cover object-top"
        src={poster}
        width={playbackId ? 828 : undefined}
        height={playbackId ? 1432 : undefined}
        unoptimized={!!playbackId}
        sizes="(max-width: 767px) 100vw, 0px"
        quality={100}
        alt={isEnglish ? "platform preview" : "Vorschau der TradeMania-Plattform"}
      />
      {canAnimate && (
        <>
          <video
            ref={videoRef}
            src={playbackId ? undefined : "/lp-long/hero-mobile-motion-de.mp4"}
            poster={typeof poster === "string" ? poster : poster.src}
            width={828}
            height={1432}
            muted
            loop
            playsInline
            preload="none"
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover object-top"
            onPlaying={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={() => setHasError(true)}
          />
          <button
            type="button"
            onClick={togglePlayback}
            aria-label={isPlaying ? "Animation pausieren" : "Animation abspielen"}
            className="absolute right-[1.5rem] bottom-[1.5rem] flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full bg-[#000E0A]/90 text-white hover:bg-[#000E0A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
            {isPlaying ? <Pause size={18} aria-hidden="true" /> : <Play size={18} aria-hidden="true" />}
          </button>
        </>
      )}
    </div>
  );
}
